import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  IconButton,
  useToast,
  Card,
  CardBody,
  FormErrorMessage,
  Text,
  Grid,
  GridItem,
  Badge,
  Flex,
  Spinner,
  Image,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Switch,
} from "@chakra-ui/react";
import {
  AddIcon,
  DeleteIcon,
  ArrowBackIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import ImageUpload from "../../helpers/ImageUpload";
import {
  getDataByIdSuperAdmin,
  postDataSuperAdmin,
  updateDataSuperAdmin,
} from "../../redux/actions/common.action";

const SECTION_OPTIONS = ["features", "industries", "resources"];

const SUB_SECTION_OPTIONS = {
  features: [
    // "Pipeline Management",
    // "Reports and Dashboards",
    // "Sales Automation",
    // "User Management & Control",
    // "Collaboration",
    // "Data Management and Security",
    // "Integration / Marketplace",
    // "Field Sales Management",
    // "Lead Management",
    // "Deals Management",
    // "Mobile App-Android IOS",
    // "Email, Meeting, Whatsapp and SMS",
    // "Calling Solutions",
    // "Account Management",
    // "Quotation Management",
    "For Admin",
    "For Sales Manager",
    "For Sales representative",
  ],
  industries: [
    "Education",
    "Finance",
    "Manufacturing",
    "Travel",
    "IT/ITES",
    "Digital Marketing",
    "Other Industries",
  ],
  resources: [
    "Articles & Blogs",
    "Free Resources",
    "Webinars",
    "Customer Stories",
    "How to Videos",
  ],
};

interface PageSection {
  sectionTitle: string;
  image: string;
  content: string;
  order: number;
}

const AddEditNavbarContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [deleteSectionIndex, setDeleteSectionIndex] = useState<number | null>(
    null,
  );

  const { navbarContent, loading } = useSelector((state: any) => state.common);

  const [formData, setFormData] = useState({
    section: "",
    subSection: "",
    title: "",
  });

  const [pageSections, setPageSections] = useState<PageSection[]>([
    { sectionTitle: "", image: "", content: "", order: 0 },
  ]);

  const [errors, setErrors] = useState<any>({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const [faqs, setFaqs] = useState([{ question: "", answer: "", order: 0 }]);

  const [subFeature, setSubFeature] = useState({
    image: "",
    name: "",
    featured: [""],
  });

  const [benefits, setBenefits] = useState({
    name: "",
    sections: [{ image: "", title: "", description: "", order: 0 }],
  });

  const [industriesData, setIndustriesData] = useState({
    heading: "",
    industries: [{ image: "", title: "", description: "", order: 0 }],
  });

  const [showFAQs, setShowFAQs] = useState(false);
  const [showSubFeature, setShowSubFeature] = useState(false);
  const [showBenefits, setShowBenefits] = useState(false);
  const [showIndustries, setShowIndustries] = useState(false);

  useEffect(() => {
    if (id) {
      setDataLoaded(false);
      dispatch(getDataByIdSuperAdmin(id, "navbar-content") as any);
    } else {
      setDataLoaded(true);
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (id && navbarContent?.Data) {
      setFormData({
        section: navbarContent.Data.section || "",
        subSection: navbarContent.Data.subSection || "",
        title: navbarContent.Data.title || "",
      });
      setPageSections(
        navbarContent.Data.pageSections?.length > 0
          ? navbarContent.Data.pageSections.map((section: any) => ({
              sectionTitle: section.sectionTitle || "",
              image: section.image || "",
              content: section.content || "",
              order: section.order || 0,
            }))
          : [{ sectionTitle: "", image: "", content: "", order: 0 }],
      );

      setFaqs(
        navbarContent.Data.faqs?.length > 0
          ? navbarContent.Data.faqs
          : [{ question: "", answer: "", order: 0 }],
      );

      setSubFeature(
        navbarContent.Data.subFeature || {
          image: "",
          name: "",
          featured: [""],
        },
      );

      setBenefits(
        navbarContent.Data.benefits || {
          name: "",
          sections: [{ image: "", title: "", description: "", order: 0 }],
        },
      );

      setIndustriesData(
        navbarContent.Data.industriesData || {
          heading: "",
          industries: [{ image: "", title: "", description: "", order: 0 }],
        },
      );

      setShowFAQs(navbarContent.Data.showFAQs || false);
      setShowSubFeature(navbarContent.Data.showSubFeature || false);
      setShowBenefits(navbarContent.Data.showBenefits || false);
      setShowIndustries(navbarContent.Data.showIndustries || false);

      setEditorKey((prev) => prev + 1);
      setDataLoaded(true);
    }
  }, [id, navbarContent]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "section") {
      setFormData((prev) => ({ ...prev, subSection: "" }));
    }

    setErrors((prev: any) => ({ ...prev, [name]: "" }));
  };

  const handleAddSection = () => {
    setPageSections([
      ...pageSections,
      {
        sectionTitle: "",
        image: "",
        content: "",
        order: pageSections.length,
      },
    ]);
    setEditorKey((prev) => prev + 1);
  };

  const handleRemoveSection = (index: number) => {
    if (pageSections.length > 1) {
      setDeleteSectionIndex(index);
      onOpen();
    }
  };

  const confirmDeleteSection = () => {
    if (deleteSectionIndex !== null) {
      setPageSections(pageSections.filter((_, i) => i !== deleteSectionIndex));
      setEditorKey((prev) => prev + 1);
      setDeleteSectionIndex(null);
      onClose();
    }
  };

  const handleSectionChange = (
    index: number,
    field: keyof PageSection,
    value: string,
  ) => {
    const updatedSections = [...pageSections];
    updatedSections[index] = {
      ...updatedSections[index],
      [field]: value,
    };
    setPageSections(updatedSections);
  };

  const handleImageUpload = (index: number, url: string) => {
    handleSectionChange(index, "image", url);
  };

  const handleRemoveImage = (index: number) => {
    handleSectionChange(index, "image", "");
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.section) newErrors.section = "Section is required";
    if (!formData.subSection) newErrors.subSection = "Sub-section is required";
    if (!formData.title) newErrors.title = "Title is required";

    pageSections.forEach((section, index) => {
      if (!section.sectionTitle) {
        newErrors[`section_${index}_title`] = "Section title is required";
      }
      if (!section.content) {
        newErrors[`section_${index}_content`] = "Content is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        status: "error",
        position: "top-right",
        isClosable: true,
      });
      return;
    }

    const payload = {
      ...formData,
      pageSections: pageSections.map((section, index) => ({
        ...section,
        order: index,
      })),
      faqs: faqs.map((faq, index) => ({
        ...faq,
        order: index,
      })),
      subFeature,
      benefits: {
        ...benefits,
        sections: benefits.sections.map((sec, index) => ({
          ...sec,
          order: index,
        })),
      },
      industriesData: {
        ...industriesData,
        industries: industriesData.industries.map((ind, index) => ({
          ...ind,
          order: index,
        })),
      },
      showFAQs,
      showSubFeature,
      showBenefits,
      showIndustries,
    };

    try {
      if (id) {
        await dispatch(
          updateDataSuperAdmin(
            id,
            "navbar-content",
            payload,
            navigate,
            toast,
          ) as any,
        );
      } else {
        await dispatch(
          postDataSuperAdmin(payload, "navbar-content", navigate, toast) as any,
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  if (id && !dataLoaded) {
    return (
      <Box p="8px" bg="gray.50" minH="100vh">
        <Flex justify="center" align="center" minH="60vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.600" fontSize="sm">
              Loading content...
            </Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Box p="8px" bg="gray.50" minH="100vh">
      {/* Header with back button and mode badge */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        p="12px"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <IconButton
              aria-label="Go back"
              icon={<ArrowBackIcon />}
              onClick={() => navigate("/navbar-content-list")}
              variant="ghost"
              size="sm"
              _hover={{ bg: "gray.100" }}
            />
          </HStack>
          <Badge
            colorScheme={id ? "blue" : "green"}
            fontSize="xs"
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="medium"
          >
            {id ? "Edit Mode" : "Create Mode"}
          </Badge>
        </Flex>
      </Box>

      <form onSubmit={handleSubmit}>
        {/* Basic Information Card */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
          mb="8px"
          p={4}
        >
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
            Basic Information
          </Text>

          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
            <GridItem>
              <FormControl isInvalid={!!errors.section} isRequired size="sm">
                <FormLabel fontSize="xs" fontWeight="medium" color="gray.700">
                  Section
                </FormLabel>
                <Select
                  name="section"
                  value={formData.section}
                  onChange={handleInputChange}
                  placeholder="Select section"
                  size="sm"
                  fontSize="xs"
                  borderRadius="md"
                  borderColor="gray.300"
                >
                  {SECTION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage fontSize="xs">
                  {errors.section}
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isInvalid={!!errors.subSection} isRequired size="sm">
                <FormLabel fontSize="xs" fontWeight="medium" color="gray.700">
                  Sub-section
                </FormLabel>
                <Select
                  name="subSection"
                  value={formData.subSection}
                  onChange={handleInputChange}
                  placeholder="Select sub-section"
                  isDisabled={!formData.section}
                  size="sm"
                  fontSize="xs"
                  borderRadius="md"
                  borderColor="gray.300"
                >
                  {formData.section &&
                    SUB_SECTION_OPTIONS[
                      formData.section as keyof typeof SUB_SECTION_OPTIONS
                    ]?.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                </Select>
                <FormErrorMessage fontSize="xs">
                  {errors.subSection}
                </FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isInvalid={!!errors.title} isRequired size="sm">
                <FormLabel fontSize="xs" fontWeight="medium" color="gray.700">
                  Title
                </FormLabel>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter content title"
                  size="sm"
                  fontSize="xs"
                  borderRadius="md"
                  borderColor="gray.300"
                />
                <FormErrorMessage fontSize="xs">
                  {errors.title}
                </FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
        </Box>

        {/* Page Sections */}
        {pageSections.map((section, index) => (
          <Box
            key={`section-${index}-${editorKey}`}
            bg="white"
            borderRadius="xl"
            boxShadow="sm"
            border="1px"
            borderColor="gray.200"
            mb="8px"
            p={4}
          >
            <Flex justify="space-between" align="center" mb={3}>
              <Badge
                colorScheme="purple"
                fontSize="xs"
                px={2}
                py={1}
                borderRadius="full"
                fontWeight="medium"
              >
                Section {index + 1}
              </Badge>
              {pageSections.length > 1 && (
                <IconButton
                  aria-label="Remove section"
                  icon={<DeleteIcon />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleRemoveSection(index)}
                  _hover={{ bg: "red.50" }}
                />
              )}
            </Flex>

            <VStack spacing={3} align="stretch">
              <Grid templateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={3}>
                <GridItem>
                  <FormControl
                    isInvalid={!!errors[`section_${index}_title`]}
                    isRequired
                    size="sm"
                  >
                    <FormLabel
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.700"
                    >
                      Section Title
                    </FormLabel>
                    <Input
                      value={section.sectionTitle}
                      onChange={(e) =>
                        handleSectionChange(
                          index,
                          "sectionTitle",
                          e.target.value,
                        )
                      }
                      placeholder="Enter section title"
                      size="sm"
                      fontSize="xs"
                      borderRadius="md"
                      borderColor="gray.300"
                    />
                    <FormErrorMessage fontSize="xs">
                      {errors[`section_${index}_title`]}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl size="sm">
                    <FormLabel
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.700"
                    >
                      Image (Optional)
                    </FormLabel>
                    {section.image ? (
                      <Box position="relative">
                        <Image
                          src={section.image}
                          alt="Uploaded"
                          borderRadius="md"
                          maxH="100px"
                          objectFit="cover"
                          border="1px"
                          borderColor="gray.300"
                        />
                        <IconButton
                          aria-label="Remove image"
                          icon={<CloseIcon />}
                          size="xs"
                          position="absolute"
                          top={1}
                          right={1}
                          colorScheme="red"
                          onClick={() => handleRemoveImage(index)}
                        />
                      </Box>
                    ) : (
                      <Box
                        bg="gray.50"
                        p={2}
                        borderRadius="md"
                        border="1px"
                        borderColor="gray.300"
                      >
                        <ImageUpload
                          onImageUpload={(url: any) =>
                            handleImageUpload(index, url)
                          }
                        />
                      </Box>
                    )}
                  </FormControl>
                </GridItem>
              </Grid>

              <FormControl
                isInvalid={!!errors[`section_${index}_content`]}
                isRequired
              >
                <FormLabel fontSize="xs" fontWeight="medium" color="gray.700">
                  Content
                </FormLabel>
                <Box
                  border="1px"
                  borderColor="gray.300"
                  borderRadius="md"
                  overflow="hidden"
                  minH="250px"
                >
                  <Box className="ck-editor-wrapper">
                    <CKEditor
                      key={`editor-${index}-${editorKey}`}
                      editor={ClassicEditor}
                      data={section.content || ""}
                      onChange={(event: any, editor: any) => {
                        const data = editor.getData();
                        handleSectionChange(index, "content", data);
                      }}
                      config={{
                        toolbar: [
                          "heading",
                          "|",
                          "bold",
                          "italic",
                          "link",
                          "bulletedList",
                          "numberedList",
                          "|",
                          "blockQuote",
                          "insertTable",
                          "|",
                          "undo",
                          "redo",
                        ],
                      }}
                    />
                  </Box>
                </Box>
                <FormErrorMessage fontSize="xs">
                  {errors[`section_${index}_content`]}
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </Box>
        ))}
        {/* Add Section Button */}
        <Button
          leftIcon={<AddIcon />}
          onClick={handleAddSection}
          size="sm"
          fontSize="xs"
          variant="outline"
          borderColor="blue.500"
          color="blue.500"
          _hover={{ bg: "blue.50" }}
          mb="8px"
          w="full"
        >
          Add Section
        </Button>
        {/* FAQs Section */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
          mb="8px"
          p={4}
        >
          <Flex justify="space-between" align="center" mb={3}>
            <HStack>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                FAQs
              </Text>
              <Switch
                colorScheme="green"
                isChecked={showFAQs}
                onChange={(e) => setShowFAQs(e.target.checked)}
              />
              <Text fontSize="xs" color={showFAQs ? "green.600" : "gray.500"}>
                {showFAQs ? "Visible" : "Hidden"}
              </Text>
            </HStack>
            {showFAQs && (
              <Button
                leftIcon={<AddIcon />}
                size="xs"
                onClick={() =>
                  setFaqs([
                    ...faqs,
                    { question: "", answer: "", order: faqs.length },
                  ])
                }
              >
                Add FAQ
              </Button>
            )}
          </Flex>

          {showFAQs && (
            <>
              {faqs.map((faq, index) => (
                <Box key={index} mb={3} p={3} bg="gray.50" borderRadius="md">
                  <Flex justify="space-between" mb={2}>
                    <Badge colorScheme="cyan">FAQ {index + 1}</Badge>
                    {faqs.length > 1 && (
                      <IconButton
                        aria-label="Delete FAQ"
                        icon={<DeleteIcon />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() =>
                          setFaqs(faqs.filter((_, i) => i !== index))
                        }
                      />
                    )}
                  </Flex>
                  <VStack spacing={2}>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs">Question</FormLabel>
                      <Input
                        value={faq.question}
                        onChange={(e) => {
                          const updated = [...faqs];
                          updated[index].question = e.target.value;
                          setFaqs(updated);
                        }}
                        size="sm"
                        fontSize="xs"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs">Answer</FormLabel>
                      <Input
                        value={faq.answer}
                        onChange={(e) => {
                          const updated = [...faqs];
                          updated[index].answer = e.target.value;
                          setFaqs(updated);
                        }}
                        size="sm"
                        fontSize="xs"
                        as="textarea"
                        rows={5}
                      />
                    </FormControl>
                  </VStack>
                </Box>
              ))}
            </>
          )}

          {!showFAQs && (
            <Text fontSize="xs" color="gray.500" textAlign="center" py={2}>
              FAQs section is hidden. Enable the toggle to add content.
            </Text>
          )}
        </Box>

        {/* Sub Feature Section */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
          mb="8px"
          p={4}
        >
          <Flex justify="space-between" align="center" mb={3}>
            <HStack>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Sub Feature
              </Text>
              <Switch
                colorScheme="green"
                isChecked={showSubFeature}
                onChange={(e) => setShowSubFeature(e.target.checked)}
              />
              <Text
                fontSize="xs"
                color={showSubFeature ? "green.600" : "gray.500"}
              >
                {showSubFeature ? "Visible" : "Hidden"}
              </Text>
            </HStack>
          </Flex>

          {showSubFeature && (
            <VStack spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs">Image URL</FormLabel>
                {subFeature.image ? (
                  <Box position="relative">
                    <Image
                      src={subFeature.image}
                      alt="Sub Feature"
                      borderRadius="md"
                      maxH="100px"
                      objectFit="cover"
                    />
                    <IconButton
                      aria-label="Remove sub feature image"
                      icon={<CloseIcon />}
                      size="xs"
                      position="absolute"
                      top={1}
                      right={1}
                      colorScheme="red"
                      onClick={() =>
                        setSubFeature({ ...subFeature, image: "" })
                      }
                    />
                  </Box>
                ) : (
                  <ImageUpload
                    onImageUpload={(url) =>
                      setSubFeature({ ...subFeature, image: url })
                    }
                  />
                )}
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="xs">Name</FormLabel>
                <Input
                  value={subFeature.name}
                  onChange={(e) =>
                    setSubFeature({ ...subFeature, name: e.target.value })
                  }
                  size="sm"
                  fontSize="xs"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs">Featured Items</FormLabel>
                {subFeature.featured.map((item, idx) => (
                  <HStack key={idx} mb={2}>
                    <Input
                      value={item}
                      onChange={(e) => {
                        const updated = [...subFeature.featured];
                        updated[idx] = e.target.value;
                        setSubFeature({ ...subFeature, featured: updated });
                      }}
                      size="sm"
                      fontSize="xs"
                    />
                    {subFeature.featured.length > 1 && (
                      <IconButton
                        aria-label="Remove featured item"
                        icon={<DeleteIcon />}
                        size="xs"
                        onClick={() => {
                          const updated = subFeature.featured.filter(
                            (_, i) => i !== idx,
                          );
                          setSubFeature({ ...subFeature, featured: updated });
                        }}
                      />
                    )}
                  </HStack>
                ))}
                <Button
                  size="xs"
                  onClick={() =>
                    setSubFeature({
                      ...subFeature,
                      featured: [...subFeature.featured, ""],
                    })
                  }
                >
                  Add Item
                </Button>
              </FormControl>
            </VStack>
          )}

          {!showSubFeature && (
            <Text fontSize="xs" color="gray.500" textAlign="center" py={2}>
              Sub Feature section is hidden. Enable the toggle to add content.
            </Text>
          )}
        </Box>

        {/* Benefits Section */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
          mb="8px"
          p={4}
        >
          <Flex justify="space-between" align="center" mb={3}>
            <HStack>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Benefits
              </Text>
              <Switch
                colorScheme="green"
                isChecked={showBenefits}
                onChange={(e) => setShowBenefits(e.target.checked)}
              />
              <Text
                fontSize="xs"
                color={showBenefits ? "green.600" : "gray.500"}
              >
                {showBenefits ? "Visible" : "Hidden"}
              </Text>
            </HStack>
          </Flex>

          {showBenefits && (
            <>
              <FormControl mb={3}>
                <FormLabel fontSize="xs">Section Name</FormLabel>
                <Input
                  value={benefits.name}
                  onChange={(e) =>
                    setBenefits({ ...benefits, name: e.target.value })
                  }
                  size="sm"
                  fontSize="xs"
                />
              </FormControl>

              <Button
                leftIcon={<AddIcon />}
                size="xs"
                mb={3}
                onClick={() =>
                  setBenefits({
                    ...benefits,
                    sections: [
                      ...benefits.sections,
                      {
                        image: "",
                        title: "",
                        description: "",
                        order: benefits.sections.length,
                      },
                    ],
                  })
                }
              >
                Add Benefit
              </Button>

              {benefits.sections.map((section, index) => (
                <Box key={index} mb={3} p={3} bg="gray.50" borderRadius="md">
                  <Flex justify="space-between" mb={2}>
                    <Badge colorScheme="green">Benefit {index + 1}</Badge>
                    {benefits.sections.length > 1 && (
                      <IconButton
                        aria-label="Delete benefit"
                        icon={<DeleteIcon />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => {
                          const updated = benefits.sections.filter(
                            (_, i) => i !== index,
                          );
                          setBenefits({ ...benefits, sections: updated });
                        }}
                      />
                    )}
                  </Flex>
                  <VStack spacing={2}>
                    <FormControl>
                      <FormLabel fontSize="xs">Image</FormLabel>
                      {section.image ? (
                        <Box position="relative">
                          <Image
                            src={section.image}
                            maxH="80px"
                            borderRadius="md"
                          />
                          <IconButton
                            aria-label="Remove benefit image"
                            icon={<CloseIcon />}
                            size="xs"
                            position="absolute"
                            top={1}
                            right={1}
                            colorScheme="red"
                            onClick={() => {
                              const updated = [...benefits.sections];
                              updated[index].image = "";
                              setBenefits({ ...benefits, sections: updated });
                            }}
                          />
                        </Box>
                      ) : (
                        <ImageUpload
                          onImageUpload={(url) => {
                            const updated = [...benefits.sections];
                            updated[index].image = url;
                            setBenefits({ ...benefits, sections: updated });
                          }}
                        />
                      )}
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs">Title</FormLabel>
                      <Input
                        value={section.title}
                        onChange={(e) => {
                          const updated = [...benefits.sections];
                          updated[index].title = e.target.value;
                          setBenefits({ ...benefits, sections: updated });
                        }}
                        size="sm"
                        fontSize="xs"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs">Description</FormLabel>
                      <Input
                        value={section.description}
                        onChange={(e) => {
                          const updated = [...benefits.sections];
                          updated[index].description = e.target.value;
                          setBenefits({ ...benefits, sections: updated });
                        }}
                        size="sm"
                        fontSize="xs"
                        as="textarea"
                        rows={2}
                      />
                    </FormControl>
                  </VStack>
                </Box>
              ))}
            </>
          )}

          {!showBenefits && (
            <Text fontSize="xs" color="gray.500" textAlign="center" py={2}>
              Benefits section is hidden. Enable the toggle to add content.
            </Text>
          )}
        </Box>

        {/* Industries Data Section */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
          mb="8px"
          p={4}
        >
          <Flex justify="space-between" align="center" mb={3}>
            <HStack>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Industries Data
              </Text>
              <Switch
                colorScheme="green"
                isChecked={showIndustries}
                onChange={(e) => setShowIndustries(e.target.checked)}
              />
              <Text
                fontSize="xs"
                color={showIndustries ? "green.600" : "gray.500"}
              >
                {showIndustries ? "Visible" : "Hidden"}
              </Text>
            </HStack>
          </Flex>

          {showIndustries && (
            <>
              <FormControl mb={3}>
                <FormLabel fontSize="xs">Heading</FormLabel>
                <Input
                  value={industriesData.heading}
                  onChange={(e) =>
                    setIndustriesData({
                      ...industriesData,
                      heading: e.target.value,
                    })
                  }
                  size="sm"
                  fontSize="xs"
                />
              </FormControl>

              <Button
                leftIcon={<AddIcon />}
                size="xs"
                mb={3}
                onClick={() =>
                  setIndustriesData({
                    ...industriesData,
                    industries: [
                      ...industriesData.industries,
                      {
                        image: "",
                        title: "",
                        description: "",
                        order: industriesData.industries.length,
                      },
                    ],
                  })
                }
              >
                Add Industry
              </Button>

              {industriesData.industries.map((industry, index) => (
                <Box key={index} mb={3} p={3} bg="gray.50" borderRadius="md">
                  <Flex justify="space-between" mb={2}>
                    <Badge colorScheme="orange">Industry {index + 1}</Badge>
                    {industriesData.industries.length > 1 && (
                      <IconButton
                        aria-label="Delete industry"
                        icon={<DeleteIcon />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => {
                          const updated = industriesData.industries.filter(
                            (_, i) => i !== index,
                          );
                          setIndustriesData({
                            ...industriesData,
                            industries: updated,
                          });
                        }}
                      />
                    )}
                  </Flex>
                  <VStack spacing={2}>
                    <FormControl>
                      <FormLabel fontSize="xs">Image</FormLabel>
                      {industry.image ? (
                        <Box position="relative">
                          <Image
                            src={industry.image}
                            maxH="80px"
                            borderRadius="md"
                          />
                          <IconButton
                            aria-label="Remove industry image"
                            icon={<CloseIcon />}
                            size="xs"
                            position="absolute"
                            top={1}
                            right={1}
                            colorScheme="red"
                            onClick={() => {
                              const updated = [...industriesData.industries];
                              updated[index].image = "";
                              setIndustriesData({
                                ...industriesData,
                                industries: updated,
                              });
                            }}
                          />
                        </Box>
                      ) : (
                        <ImageUpload
                          onImageUpload={(url) => {
                            const updated = [...industriesData.industries];
                            updated[index].image = url;
                            setIndustriesData({
                              ...industriesData,
                              industries: updated,
                            });
                          }}
                        />
                      )}
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs">Title</FormLabel>
                      <Input
                        value={industry.title}
                        onChange={(e) => {
                          const updated = [...industriesData.industries];
                          updated[index].title = e.target.value;
                          setIndustriesData({
                            ...industriesData,
                            industries: updated,
                          });
                        }}
                        size="sm"
                        fontSize="xs"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs">Description</FormLabel>
                      <Input
                        value={industry.description}
                        onChange={(e) => {
                          const updated = [...industriesData.industries];
                          updated[index].description = e.target.value;
                          setIndustriesData({
                            ...industriesData,
                            industries: updated,
                          });
                        }}
                        size="sm"
                        fontSize="xs"
                        as="textarea"
                        rows={2}
                      />
                    </FormControl>
                  </VStack>
                </Box>
              ))}
            </>
          )}

          {!showIndustries && (
            <Text fontSize="xs" color="gray.500" textAlign="center" py={2}>
              Industries section is hidden. Enable the toggle to add content.
            </Text>
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
          p={3}
        >
          <HStack spacing={2} justify="flex-end">
            <Button
              variant="outline"
              onClick={() => navigate("/navbar-content-list")}
              size="sm"
              fontSize="xs"
              px={6}
              borderColor="gray.300"
              _hover={{ bg: "gray.50" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              loadingText={id ? "Updating..." : "Creating..."}
              size="sm"
              fontSize="xs"
              px={6}
            >
              {id ? "Update" : "Create"}
            </Button>
          </HStack>
        </Box>
      </form>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Confirm Delete</ModalHeader>
          <ModalBody>
            <Text fontSize="sm">
              Are you sure you want to delete this section? This action cannot
              be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" size="sm" onClick={onClose} mr={2}>
              Cancel
            </Button>
            <Button colorScheme="red" size="sm" onClick={confirmDeleteSection}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <style>
        {`
          .ck-editor-wrapper .ck-editor__editable {
            min-height: 250px !important;
            max-height: 400px;
            overflow-y: auto;
            font-size: 13px;
          }
          
          .ck-editor-wrapper .ck-toolbar {
            background-color: #f7fafc !important;
            border-bottom: 1px solid #e2e8f0 !important;
            font-size: 12px;
          }
          
          .ck-editor-wrapper .ck-editor__editable:focus {
            border-color: #3182ce !important;
            box-shadow: 0 0 0 1px #3182ce !important;
          }
        `}
      </style>
    </Box>
  );
};

export default AddEditNavbarContent;
